// ============================================================
// DM-LMS — Shared Constants
// DRY: one place for all magic values.
// ============================================================

import type { PaymentMethod, Role, User } from "../types/index.js";

// ---- Dev role switcher (local development only — never active outside APP_ENV=development) ----
// Fake users share IDs/codes with seed.sql so foreign keys resolve against the seeded DB.
export const DEV_ROLE_HEADER      = "X-Dev-Role";
export const DEV_ROLE_STORAGE_KEY = "dev-role";

const DEV_SEED_DATE = "2026-08-19T00:00:00.000Z";

export const DEV_USERS = {
  student: {
    id: "50000000-0000-4000-8000-000000000001", code: "ST-0001", name: "Dev Student",
    email: "student@dev.local", phone: "+254700000101", role: "student",
    avatarUrl: null, createdAt: DEV_SEED_DATE, updatedAt: DEV_SEED_DATE,
  },
  tutor: {
    id: "70000000-0000-4000-8000-000000000001", code: "TU-0001", name: "Dev Tutor",
    email: "tutor@dev.local", phone: "+254700000011", role: "tutor",
    avatarUrl: null, createdAt: DEV_SEED_DATE, updatedAt: DEV_SEED_DATE,
  },
  admin: {
    id: "a0000000-0000-4000-8000-000000000001", code: "AD-0001", name: "Dev Admin",
    email: "admin@dev.local", phone: "+254700000001", role: "admin",
    avatarUrl: null, createdAt: DEV_SEED_DATE, updatedAt: DEV_SEED_DATE,
  },
} as const satisfies Record<Role, User>;

// ---- Activity log event types ----
export const ACTIVITY_EVENTS = {
  ENROLLMENT_CREATED: "enrollment.created",
  PAYMENT_CONFIRMED:  "payment.confirmed",
  PAYMENT_FAILED:     "payment.failed",
} as const;

// ---- Payments ----
// Used until student_profiles (preferred payment method) exists.
export const DEFAULT_PAYMENT_METHOD = "mpesa" as const satisfies PaymentMethod;

// Proof-of-payment types. These two are fixed: cash behaviour (no reference, payer/receiver/amount
// instead) and the code-match flow's automatic proof depend on the key, never on config metadata.
// Every other proof type is an admin-managed `proof_type` config row and needs a reference.
export const PROOF_TYPE_CASH = "cash";
export const PROOF_TYPE_MPESA = "mpesa";
export const PROOF_PARTY_LENGTH = { min: 2, max: 100 } as const;   // payer / receiver names
export const PROOF_REFERENCE_LENGTH = { min: 3, max: 50 } as const;

// M-Pesa transaction code the student submits and the admin verifies (real codes are 10 characters)
export const PAYMENT_REFERENCE_LENGTH = { min: 6, max: 20 } as const;

// Student payments page refreshes this often while any payment awaits verification
export const PAYMENT_POLL_INTERVAL_MS = 15_000;

// The "payment confirmed" banner dismisses itself after this long
export const PAYMENT_BANNER_DISMISS_MS = 10_000;

// ---- M-Pesa payment instructions (system_config category "mpesa_config") ----
export const MPESA_CONFIG_KEYS = {
  paybill:          "paybill",
  accountRefFormat: "account_ref_format",
  supportPhone:     "support_phone",
  stkPushEnabled:   "stk_push_enabled",   // label "true" turns automated STK push on (Milestone 2)
} as const;

export const MPESA_STK_KEY = MPESA_CONFIG_KEYS.stkPushEnabled;

// ---- Tutor email list (system_config category "tutor_email") ----
export const TUTOR_EMAIL_MAX_LENGTH = 100;   // system_config label cap
export const TUTOR_EMAIL_KEY_LENGTH = 32;    // hex chars of SHA-256 kept as the row key (128 bits; key cap is 50)

// Placeholders an admin may use inside the account_ref_format value
export const ACCOUNT_REF_PLACEHOLDERS = {
  course:  "{COURSE_REF}",
  payment: "{PAYMENT_CODE}",
} as const;

// Used when account_ref_format is missing or lacks the payment placeholder (which keeps references unique)
export const DEFAULT_ACCOUNT_REF_FORMAT = `${ACCOUNT_REF_PLACEHOLDERS.course}-${ACCOUNT_REF_PLACEHOLDERS.payment}`;

// ---- Code prefixes ----
export const CODE_PREFIXES = {
  student:     "ST",
  tutor:       "TU",
  admin:       "AD",
  course:      "CR",
  payment:     "PAY",
  certificate: "CERT",
} as const;

// ---- Pagination ----
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE     = 100;

// ---- Rate limiting ----
export const RATE_LIMITS = {
  auth:    { window: 900_000, max: 10  },  // 10 attempts per 15 min
  api:     { window: 900_000, max: 100 },  // 100 requests per 15 min
  payment: { window: 3_600_000, max: 5 },  // 5 payment attempts per hour
} as const;

// ---- Payment reminder days ----
export const PAYMENT_REMINDER_DAYS = [3, 7, 14] as const;

// ---- Payment provider retry policy ----
export const PAYMENT_RETRY_DELAY_MS = 3_000;

// ---- Email provider retry policy (reminders only — see CLAUDE.md's Error Boundaries) ----
export const EMAIL_REMINDER_RETRY_DELAY_MS = 3_600_000; // 1 hour

// ---- Session / cache TTL (seconds) ----
export const TTL = {
  session:     86_400,     // 24 hours
  courseCache: 300,        // 5 minutes
  certUrl:     3_600,      // 1 hour (signed R2 URL)
} as const;

// ---- Minimum hours for certificate eligibility ----
export const CERT_MIN_ATTENDANCE_PERCENT = 80;

// ---- Max capacity guardrail ----
export const MAX_COURSE_CAPACITY = 200;

// ---- Email subjects ----
export const EMAIL_SUBJECTS = {
  welcome:           "Welcome to Daniels Mediation LMS",
  paymentReminder:   "Action Required: Outstanding Balance — Daniels Mediation",
  paymentConfirmed:  "Payment Confirmed — Daniels Mediation",
  attendanceFollowUp:"We Missed You — Daniels Mediation",
  certificate:       "Your Certificate is Ready — Daniels Mediation",
  engagementNudge:   "Stay on Track — Daniels Mediation",
} as const;

// ---- Activity feed: shown when the actor's account has since been deleted ----
export const DELETED_USER_LABEL = "Deleted user";

// ---- HTTP status codes (avoid magic numbers) ----
export const HTTP = {
  OK:                  200,
  CREATED:             201,
  NO_CONTENT:          204,
  BAD_REQUEST:         400,
  UNAUTHORIZED:        401,
  FORBIDDEN:           403,
  NOT_FOUND:           404,
  CONFLICT:            409,
  UNPROCESSABLE:       422,
  TOO_MANY_REQUESTS:   429,
  INTERNAL:            500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// ---- Error codes ----
export const ERROR_CODES = {
  INVALID_INPUT:       "INVALID_INPUT",
  UNAUTHORIZED:        "UNAUTHORIZED",
  FORBIDDEN:           "FORBIDDEN",
  NOT_FOUND:           "NOT_FOUND",
  CONFLICT:            "CONFLICT",
  ALREADY_EXISTS:      "ALREADY_EXISTS",
  PAYMENT_FAILED:      "PAYMENT_FAILED",
  ENROLLMENT_CLOSED:   "ENROLLMENT_CLOSED",
  PREREQUISITE_UNMET:  "PREREQUISITE_UNMET",
  RATE_LIMITED:        "RATE_LIMITED",
  PROFILE_INCOMPLETE:  "PROFILE_INCOMPLETE",
  INTERNAL:            "INTERNAL_ERROR",
} as const;

export * from "./countries.js";

// Student phone: international format so non-Kenyan students can register.
// (The M-Pesa number stays +254-only; tutor and admin phones are validated elsewhere.)
export const STUDENT_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

// Kenyan (+254) numbers: tutor and admin phones and M-Pesa payments.
export const KENYAN_PHONE_PATTERN = /^\+254\d{9}$/;

// Validation messages we write ourselves, safe to show to users. The API returns a failed
// validation's message only if it is in USER_FACING_VALIDATION_MESSAGES (an explicit list, so a
// zod upgrade cannot change which messages reach users); anything else stays "Validation failed".
export const VALIDATION_MESSAGES = {
  studentPhone:   "Enter your phone number with the country code, for example +254700000000 (7 to 15 digits after the +).",
  kenyanPhone:    "Enter a Kenyan number as +254 followed by 9 digits, for example +254700000000.",
  countyRequired: "County is required for Kenya",
  regionRequired: "City, state or county is required",
  mpesaCode:      "Enter the M-Pesa code as 6 to 20 letters and digits, for example QGH7X8Y9Z0.",
  proofReference: "Enter the reference exactly as it appears on the proof, 3 to 50 characters.",
  proofParty:     "Enter a name of 2 to 100 characters.",
  proofAmount:    "Enter the amount received as a whole number of KES.",
} as const;

export const USER_FACING_VALIDATION_MESSAGES: ReadonlySet<string> = new Set(Object.values(VALIDATION_MESSAGES));

// A person's full name, as entered on the student setup form.
export const PERSON_NAME_MIN_LENGTH = 2;
export const PERSON_NAME_MAX_LENGTH = 100;

// Admin Users list: the free-text search box (name, email or code).
export const USER_SEARCH_MAX_LENGTH = 100;

// Profile fields an admin viewing ANOTHER user must not receive (the user keeps them on their own record).
export const ADMIN_HIDDEN_PROFILE_FIELDS = ["idNumber", "mpesaNumber", "photoR2Key", "rejectionReason"] as const;

// Admin detail view of an admin: how many of their own recent audit events to show.
export const ADMIN_ACTIVITY_PREVIEW_LIMIT = 20;
